import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const paise2inr = p => `₹${(Math.abs(p || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
const fmtTime = d => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS = {
  pending:   { bg: '#dbeafe', color: '#1e40af', label: 'Pending' },
  allocated: { bg: '#d1fae5', color: '#065f46', label: 'Allocated' },
  settled:   { bg: '#ecfdf5', color: '#047857', label: 'Settled' },
  forfeited: { bg: '#fee2e2', color: '#991b1b', label: 'Forfeited' },
  rejected:  { bg: '#f3f4f6', color: '#6b7280', label: 'Rejected' },
  open:      { bg: '#dbeafe', color: '#1e40af', label: 'Open' },
  closed:    { bg: '#f3f4f6', color: '#6b7280', label: 'Closed' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
};

const Spinner = () => <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />;

const ic = 'w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';
const is = { borderColor: '#d1d5db', color: '#1f2937', backgroundColor: '#fff' };
const lbl = 'block text-xs font-medium mb-1';

// ── Trader Detail Drawer ──────────────────────────────────────────────────────
const TraderDrawer = ({ trader, bids, bidVehicles, onClose }) => {
  if (!trader) return null;
  const traderBids = bids.filter(b => b.user_id === trader.user_id);
  const traderBidTotalFee = (bidId) => bidVehicles.filter(v => v.bid_id === bidId).reduce((s, v) => s + Number(v.financing_fee_paise || 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="w-full bg-white rounded-t-3xl p-5 space-y-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base" style={{ color: '#1f2937' }}>Trader Profile</h3>
          <button onClick={onClose} className="p-1"><Icon name="X" size={18} /></button>
        </div>
        <div className="space-y-2">
          {[['Name', trader.full_name], ['Company', trader.company_name], ['GSTIN', trader.gstin], ['Mobile', trader.mobile_phone], ['Email', trader.email]].map(([k, v]) => v ? (
            <div key={k} className="flex justify-between py-1.5 border-b" style={{ borderColor: '#f3f4f6' }}>
              <span className="text-xs font-medium" style={{ color: '#6b7280' }}>{k}</span>
              <span className="text-xs font-semibold text-right ml-4" style={{ color: '#1f2937' }}>{v}</span>
            </div>
          ) : null)}
        </div>
        {traderBids.length > 0 && (
          <>
            <h4 className="font-semibold text-sm" style={{ color: '#374151' }}>Bid History ({traderBids.length})</h4>
            <div className="space-y-2">
              {traderBids.map(b => (
                <div key={b.id} className="flex items-center justify-between p-2 rounded-xl" style={{ backgroundColor: '#f9fafb' }}>
                  <div>
                    <div className="text-xs font-bold" style={{ color: '#065f46' }}>{paise2inr(traderBidTotalFee(b.id))} fee</div>
                    <div className="text-xs" style={{ color: '#6b7280' }}>{fmtTime(b.created_at)}</div>
                  </div>
                  <Badge status={b.status} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const DoFinanceTab = () => {
  const [subTab, setSubTab] = useState('bid_board');
  const [factories, setFactories] = useState([]);
  const [slots, setSlots] = useState([]);
  const [bids, setBids] = useState([]);
  const [quotas, setQuotas] = useState([]);
  const [bidVehicles, setBidVehicles] = useState([]);
  const [traders, setTraders] = useState({});
  const [rateConfigs, setRateConfigs] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [applications, setApplications] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [expandedBid, setExpandedBid] = useState(null);
  const [selection, setSelection] = useState({}); // { [quotaId]: Set(vehicleId) }
  const [confirmAction, setConfirmAction] = useState(null); // { quotaId, actionKey }
  const [bidActionLoading, setBidActionLoading] = useState(false);

  const [limitAction, setLimitAction] = useState(null); // { app_id, type }
  const [vehicleLimitInput, setVehicleLimitInput] = useState('');
  const [rejectReasonInput, setRejectReasonInput] = useState('');
  const [limitActionLoading, setLimitActionLoading] = useState(false);

  const [detailTrader, setDetailTrader] = useState(null);

  const [slotForm, setSlotForm] = useState({ factory_id: '', loading_date: '', dos_available: '', notes: '' });
  const [slotCreating, setSlotCreating] = useState(false);
  const [rateForm, setRateForm] = useState({ config_type: 'global', factory_id: '', score_band: 'high', rate_paise: '', notes: '' });
  const [rateSaving, setRateSaving] = useState(false);

  const [statusFilter, setStatusFilter] = useState('active');
  const [factoryFilter, setFactoryFilter] = useState('all');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: facs }, { data: slotData }, { data: bidData }, { data: quotaData }, { data: bidVehicleData }, { data: rcs }, { data: pens }, { data: appData }, { data: evalData }] = await Promise.all([
        supabase.from('factories').select('id, name, short_code').eq('status', 'Active'),
        supabase.from('do_finance_slots').select('*').order('loading_date', { ascending: false }),
        supabase.from('do_finance_bids').select('*').order('created_at', { ascending: false }),
        supabase.from('quotas').select('id, factory_name, factory_short_code, grade, quantity, price_per_ton, created_at'),
        supabase.from('do_finance_bid_vehicles').select('*').order('created_at', { ascending: false }),
        supabase.from('do_finance_rate_config').select('*').eq('is_active', true).order('config_type'),
        supabase.from('do_finance_penalty_log').select('*').order('created_at', { ascending: false }).limit(100),
        supabase.from('trader_od_applications').select('id, user_id, gstin, status, requested_limit_amount, approved_vehicle_limit, limit_status, rejection_reason, created_at').order('created_at', { ascending: false }),
        supabase.from('trader_od_evaluations').select('application_id, user_id, sugar_score, sugar_score_band, trade_score, trade_score_band').order('scored_at', { ascending: false }),
      ]);
      setFactories(facs ?? []);
      setSlots(slotData ?? []);
      setBids(bidData ?? []);
      setQuotas(quotaData ?? []);
      setBidVehicles(bidVehicleData ?? []);
      setRateConfigs(rcs ?? []);
      setPenalties(pens ?? []);
      setApplications(appData ?? []);
      const evalMap = {};
      (evalData ?? []).forEach(e => { evalMap[e.application_id] = e; });
      setEvaluations(evalMap);

      if (bidData?.length || pens?.length || appData?.length) {
        const uids = [...new Set([
          ...(bidData ?? []).map(b => b.user_id),
          ...(pens ?? []).map(p => p.user_id),
          ...(appData ?? []).map(a => a.user_id),
        ])].filter(Boolean);
        if (uids.length) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('user_id, full_name, company_name, gstin, mobile_phone, email')
            .in('user_id', uids);
          const map = {};
          (profiles ?? []).forEach(p => { map[p.user_id] = p; });
          setTraders(map);
        }
      }
    } catch (e) {
      showToast(e.message ?? 'Load failed', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doBidAction = async (fnName, body, quotaId) => {
    setBidActionLoading(true);
    try {
      const { error } = await supabase.functions.invoke(fnName, { body });
      if (error) throw error;
      showToast('Done ✓');
      setSelection(prev => ({ ...prev, [quotaId]: new Set() }));
      setConfirmAction(null);
      await fetchAll();
    } catch (e) {
      showToast(e.message ?? 'Action failed', 'error');
    } finally {
      setBidActionLoading(false);
    }
  };

  const getSelected = (quotaId) => selection[quotaId] || new Set();

  const toggleVehicleSelection = (quota, vehicle, allVehiclesForQuota) => {
    const sel = new Set(getSelected(quota.id));
    if (sel.has(vehicle.id)) {
      sel.delete(vehicle.id);
    } else {
      const selectedVehicles = allVehiclesForQuota.filter(v => sel.has(v.id));
      const currentGroupStatus = selectedVehicles[0]?.status;
      if (currentGroupStatus && currentGroupStatus !== vehicle.status) {
        sel.clear(); // switching status group — start fresh
      }
      sel.add(vehicle.id);
    }
    setSelection(prev => ({ ...prev, [quota.id]: sel }));
  };

  const clearBidSelection = (quotaId) => {
    setSelection(prev => ({ ...prev, [quotaId]: new Set() }));
    setConfirmAction(null);
  };

  const ACTIONS_FOR_STATUS = {
    pending:   [{ key: 'allocate', label: 'Allocate Selected', bg: '#065f46', fn: 'do-finance-bid-allocate' }, { key: 'reject', label: 'Reject Selected', bg: '#dc2626', fn: 'do-finance-bid-reject' }],
    allocated: [{ key: 'settle', label: 'Settle Selected', bg: '#92400e', fn: 'do-finance-bid-settle' }, { key: 'forfeit', label: 'Forfeit Selected', bg: '#dc2626', fn: 'do-finance-bid-forfeit' }],
  };

  const doLimitAction = async (fnName, body) => {
    setLimitActionLoading(true);
    try {
      const { error } = await supabase.functions.invoke(fnName, { body });
      if (error) throw error;
      showToast('Done ✓');
      setLimitAction(null);
      setVehicleLimitInput('');
      setRejectReasonInput('');
      await fetchAll();
    } catch (e) {
      showToast(e.message ?? 'Action failed', 'error');
    } finally {
      setLimitActionLoading(false);
    }
  };

  const toggleLimitAction = (app, type) => {
    if (limitAction?.app_id === app.id && limitAction?.type === type) {
      setLimitAction(null);
    } else {
      if (type === 'increase' || type === 'decrease') setVehicleLimitInput(String(app.approved_vehicle_limit ?? ''));
      if (type === 'approve') setVehicleLimitInput('');
      if (type === 'reject') setRejectReasonInput('');
      setLimitAction({ app_id: app.id, type });
    }
  };

  const createSlot = async () => {
    if (!slotForm.factory_id || !slotForm.loading_date || !slotForm.dos_available) {
      showToast('Factory, date and DOs required', 'error'); return;
    }
    setSlotCreating(true);
    try {
      const { error } = await supabase.functions.invoke('do-finance-slot-create', {
        body: { ...slotForm, dos_available: parseInt(slotForm.dos_available) }
      });
      if (error) throw error;
      showToast('Slot created ✓');
      setSlotForm({ factory_id: '', loading_date: '', dos_available: '', notes: '' });
      await fetchAll();
    } catch (e) {
      showToast(e.message ?? 'Failed', 'error');
    } finally {
      setSlotCreating(false);
    }
  };

  const saveRate = async () => {
    if (!rateForm.rate_paise) { showToast('Rate required', 'error'); return; }
    setRateSaving(true);
    try {
      const { error } = await supabase.functions.invoke('do-finance-rate-config-set', {
        body: {
          config_type: rateForm.config_type,
          rate_paise: parseInt(rateForm.rate_paise),
          factory_id: rateForm.config_type === 'factory' ? rateForm.factory_id : undefined,
          score_band: rateForm.config_type === 'score_band' ? rateForm.score_band : undefined,
          notes: rateForm.notes || undefined,
        }
      });
      if (error) throw error;
      showToast('Saved ✓');
      await fetchAll();
    } catch (e) {
      showToast(e.message ?? 'Failed', 'error');
    } finally {
      setRateSaving(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const factoryMap = Object.fromEntries(factories.map(f => [f.id, f]));
  const slotMap = Object.fromEntries(slots.map(s => [s.id, s]));

  // ── Quota-grouped Bid Board data ──────────────────────────────────────────
  const quotaMap = Object.fromEntries(quotas.map(q => [q.id, q]));
  const vehiclesByBid = bidVehicles.reduce((acc, v) => {
    if (!acc[v.bid_id]) acc[v.bid_id] = [];
    acc[v.bid_id].push(v);
    return acc;
  }, {});
  const bidsWithVehicles = bids
    .filter(b => b.quota_id)
    .map(b => ({ ...b, vehicles: vehiclesByBid[b.id] || [] }))
    .filter(b => b.vehicles.length > 0);

  const bidDerivedStatus = (vehicles) => {
    const statuses = [...new Set(vehicles.map(v => v.status))];
    return statuses.length === 1 ? statuses[0] : 'mixed';
  };
  const matchesStatusFilter = (status) => {
    if (statusFilter === 'active') return ['pending', 'allocated'].includes(status);
    if (statusFilter === 'all') return true;
    return status === statusFilter;
  };

  const bidsByQuota = bidsWithVehicles.reduce((acc, bid) => {
    if (!acc[bid.quota_id]) acc[bid.quota_id] = [];
    acc[bid.quota_id].push(bid);
    return acc;
  }, {});

  const quotaMatchesFactoryFilter = (quotaId) => {
    if (factoryFilter === 'all') return true;
    const q = quotaMap[quotaId];
    const fac = factories.find(f => f.short_code === q?.factory_short_code || f.name === q?.factory_name);
    return fac?.id === factoryFilter;
  };

  const activeQuotaIds = Object.keys(bidsByQuota)
    .filter(qid => quotaMatchesFactoryFilter(qid) && bidsByQuota[qid].some(b => matchesStatusFilter(bidDerivedStatus(b.vehicles))))
    .sort((a, b) => (quotaMap[b]?.created_at ?? '').localeCompare(quotaMap[a]?.created_at ?? ''));

  const SUB_TABS = [
    { key: 'bid_board', label: 'Bid Board', icon: 'LayoutList' },
    // 'slots' and 'rate' tabs hidden — dead (old slot/rate-config model, bypassed).
    // Code kept intentionally (createSlot, saveRate, slotForm, rateForm, JSX below) per KK — hide only, don't delete.
    { key: 'penalties', label: 'Penalties',  icon: 'AlertTriangle' },
    { key: 'bidders',   label: 'Bidders',    icon: 'Users' },
  ];

  const STATUS_FILTERS = ['active', 'pending', 'allocated', 'settled', 'forfeited', 'all'];

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-xl"
          style={{ backgroundColor: toast.type === 'error' ? '#dc2626' : '#065f46' }}>
          {toast.msg}
        </div>
      )}

      {/* Trader detail drawer */}
      <TraderDrawer trader={detailTrader} bids={bids} bidVehicles={bidVehicles} onClose={() => setDetailTrader(null)} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>DO Finance</h2>
          <p className="text-xs" style={{ color: '#6b7280' }}>
            {bidVehicles.filter(v => v.status === 'pending').length} vehicles pending · {bidVehicles.filter(v => v.status === 'allocated').length} allocated · {slots.filter(s => s.status === 'open').length} open slots
          </p>
        </div>
        <button onClick={fetchAll} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition"
          style={{ borderColor: '#e5e7eb', color: '#374151' }}>
          {loading ? <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> : <Icon name="RefreshCw" size={13} />}
          Refresh
        </button>
      </div>

      {/* Sub-tab nav */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all"
            style={{ backgroundColor: subTab === t.key ? '#1e4a72' : '#f3f4f6', color: subTab === t.key ? '#fff' : '#374151' }}>
            <Icon name={t.icon} size={12} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── BID BOARD ─────────────────────────────────────────────────── */}
      {subTab === 'bid_board' && (
        <div className="space-y-3">
          {/* Status filter pills */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all capitalize"
                style={{ backgroundColor: statusFilter === s ? '#1e4a72' : '#f3f4f6', color: statusFilter === s ? '#fff' : '#374151' }}>
                {s}
              </button>
            ))}
          </div>

          {/* Factory filter */}
          <select value={factoryFilter} onChange={e => setFactoryFilter(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border text-sm" style={is}>
            <option value="all">All Factories</option>
            {factories.map(f => <option key={f.id} value={f.id}>{f.name} ({f.short_code})</option>)}
          </select>

          {loading && <div className="text-center py-10 text-sm" style={{ color: '#6b7280' }}>Loading bids...</div>}

          {!loading && activeQuotaIds.length === 0 && (
            <div className="text-center py-14 space-y-2" style={{ color: '#9ca3af' }}>
              <Icon name="Inbox" size={36} />
              <p className="text-sm">No bids for this filter</p>
            </div>
          )}

          {!loading && activeQuotaIds.map(quotaId => {
            const quota = quotaMap[quotaId];
            if (!quota) return null;
            const visibleBids = bidsByQuota[quotaId].filter(b => matchesStatusFilter(bidDerivedStatus(b.vehicles)));
            if (visibleBids.length === 0) return null;

            const committedQty = bidsByQuota[quotaId]
              .flatMap(b => b.vehicles)
              .filter(v => !['forfeited', 'rejected'].includes(v.status))
              .reduce((sum, v) => sum + Number(v.quantity_mt), 0);

            const sel = getSelected(quotaId);
            const allQuotaVehicles = bidsByQuota[quotaId].flatMap(b => b.vehicles);
            const selectedVehicles = allQuotaVehicles.filter(v => sel.has(v.id));
            const selGroupStatus = selectedVehicles[0]?.status;
            const selTotalFee = selectedVehicles.reduce((s, v) => s + Number(v.financing_fee_paise), 0);
            const actions = ACTIONS_FOR_STATUS[selGroupStatus] || [];
            const isConfirmingHere = confirmAction?.quotaId === quotaId;

            return (
              <div key={quotaId} className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                {/* Quota header */}
                <div className="px-4 py-3" style={{ backgroundColor: '#fffbf5', borderBottom: '1px solid #f3f4f6' }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm truncate" style={{ color: '#1f2937' }}>{quota.factory_name}</span>
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded shrink-0" style={{ backgroundColor: '#e5e7eb', color: '#374151' }}>{quota.factory_short_code}</span>
                        <span className="text-xs font-semibold shrink-0" style={{ color: '#92400e' }}>{quota.grade}</span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                        ₹{Number(quota.price_per_ton).toLocaleString('en-IN')}/ton · {committedQty}/{quota.quantity} MT committed
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-bold" style={{ color: '#065f46' }}>{visibleBids.length} bid{visibleBids.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </div>

                {/* Bids */}
                <div>
                  {visibleBids.map(bid => {
                    const trader = traders[bid.user_id];
                    const isExpanded = expandedBid === bid.id;
                    const totalQty = bid.vehicles.reduce((s, v) => s + Number(v.quantity_mt), 0);
                    const totalFee = bid.vehicles.reduce((s, v) => s + Number(v.financing_fee_paise), 0);
                    const derivedStatus = bidDerivedStatus(bid.vehicles);

                    return (
                      <div key={bid.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <button onClick={() => setExpandedBid(isExpanded ? null : bid.id)}
                          className="w-full px-4 py-3 flex items-center justify-between gap-2 text-left">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon name="Truck" size={14} style={{ color: '#9ca3af' }} className="shrink-0" />
                            <div className="min-w-0">
                              <div className="font-semibold text-sm truncate" style={{ color: '#1f2937' }}>{trader?.full_name ?? '—'}</div>
                              <div className="text-xs truncate" style={{ color: '#6b7280' }}>{trader?.company_name ?? '—'} · {bid.vehicles.length} vehicle{bid.vehicles.length !== 1 ? 's' : ''} · {totalQty} MT</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="text-right">
                              <div className="text-xs font-bold" style={{ color: '#065f46' }}>{paise2inr(totalFee)}</div>
                              <Badge status={derivedStatus} />
                            </div>
                            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} style={{ color: '#9ca3af' }} />
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-3 space-y-1.5">
                            {bid.vehicles.map((v, idx) => {
                              const isSelected = sel.has(v.id);
                              const isSelectable = !['settled', 'rejected'].includes(v.status);
                              const isDimmed = selGroupStatus && selGroupStatus !== v.status && !isSelected;
                              return (
                                <label key={v.id}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-opacity"
                                  style={{
                                    backgroundColor: isSelected ? '#f0fdf4' : '#f9fafb',
                                    border: isSelected ? '1px solid #6ee7b7' : '1px solid transparent',
                                    opacity: isDimmed ? 0.45 : 1,
                                  }}>
                                  {isSelectable ? (
                                    <input type="checkbox" checked={isSelected}
                                      onChange={() => toggleVehicleSelection(quota, v, allQuotaVehicles)}
                                      className="w-4 h-4 rounded shrink-0" style={{ accentColor: '#065f46' }} />
                                  ) : <div className="w-4 shrink-0" />}
                                  <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                                    <span className="text-xs font-medium truncate" style={{ color: '#374151' }}>Vehicle {idx + 1} · {v.quantity_mt} MT</span>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className="text-xs font-bold" style={{ color: '#065f46' }}>{paise2inr(v.financing_fee_paise)} fee</span>
                                      <Badge status={v.status} />
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selection action bar */}
                {selectedVehicles.length > 0 && (
                  <div className="px-4 py-3 space-y-2" style={{ backgroundColor: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-semibold" style={{ color: '#065f46' }}>
                        {selectedVehicles.length} vehicle{selectedVehicles.length !== 1 ? 's' : ''} selected · {paise2inr(selTotalFee)} total fee
                      </span>
                      <button onClick={() => clearBidSelection(quotaId)} className="text-xs flex items-center gap-1" style={{ color: '#9ca3af' }}>
                        <Icon name="X" size={12} /> Clear
                      </button>
                    </div>

                    {!isConfirmingHere ? (
                      <div className="flex gap-2 flex-wrap">
                        {actions.map(a => (
                          <button key={a.key} onClick={() => setConfirmAction({ quotaId, actionKey: a.key })}
                            className="flex-1 min-w-[120px] py-2 rounded-lg text-xs font-bold text-white"
                            style={{ backgroundColor: a.bg }}>
                            {a.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2.5 rounded-xl flex-wrap" style={{ backgroundColor: '#fff', border: '1px solid #bbf7d0' }}>
                        <span className="text-xs font-medium" style={{ color: '#374151' }}>
                          Confirm {confirmAction.actionKey} for {selectedVehicles.length} vehicle{selectedVehicles.length !== 1 ? 's' : ''}?
                        </span>
                        <button onClick={() => {
                            const action = actions.find(a => a.key === confirmAction.actionKey);
                            if (!action) return;
                            const vehicleIds = [...selectedVehicles.map(v => v.id)];
                            doBidAction(action.fn, { vehicle_ids: vehicleIds }, quotaId);
                          }}
                          disabled={bidActionLoading}
                          className="ml-auto px-3 py-1 rounded-lg text-xs font-bold text-white flex items-center gap-1" style={{ backgroundColor: '#065f46' }}>
                          {bidActionLoading ? <Spinner /> : <Icon name="Check" size={12} />} Confirm
                        </button>
                        <button onClick={() => setConfirmAction(null)}><Icon name="X" size={14} style={{ color: '#9ca3af' }} /></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}


      {/* ── SLOTS ──────────────────────────────────────────────────────── */}
      {subTab === 'slots' && (
        <div className="space-y-4">
          {/* Create slot */}
          <div className="bg-white rounded-2xl border shadow-sm p-4 space-y-3" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Create Slot</h3>
            <div>
              <label className={lbl} style={{ color: '#374151' }}>Factory</label>
              <select value={slotForm.factory_id} onChange={e => setSlotForm(p => ({ ...p, factory_id: e.target.value }))} className={ic} style={is}>
                <option value="">Select factory</option>
                {factories.map(f => <option key={f.id} value={f.id}>{f.name} ({f.short_code})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl} style={{ color: '#374151' }}>Loading Date</label>
                <input type="date" value={slotForm.loading_date} onChange={e => setSlotForm(p => ({ ...p, loading_date: e.target.value }))} className={ic} style={is} />
              </div>
              <div>
                <label className={lbl} style={{ color: '#374151' }}>DOs Available</label>
                <input type="number" min="1" value={slotForm.dos_available} onChange={e => setSlotForm(p => ({ ...p, dos_available: e.target.value }))} placeholder="e.g. 5" className={ic} style={is} />
              </div>
            </div>
            <button onClick={createSlot} disabled={slotCreating}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: '#1e4a72' }}>
              {slotCreating ? <Spinner /> : <Icon name="Plus" size={14} />}
              Create Slot
            </button>
          </div>

          {/* Slots list */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
              <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>All Slots ({slots.length})</h3>
            </div>
            {slots.length === 0 ? (
              <div className="text-center py-10 text-sm" style={{ color: '#6b7280' }}>No slots yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Factory</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Date</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>DOs</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Bids</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map(slot => {
                      const fac = factoryMap[slot.factory_id];
                      const count = bids.filter(b => b.slot_id === slot.id).length;
                      return (
                        <tr key={slot.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td className="px-3 py-2.5">
                            <div className="font-semibold" style={{ color: '#1f2937' }}>{fac?.name ?? '—'}</div>
                            <div style={{ color: '#9ca3af' }}>{fac?.short_code}</div>
                          </td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{fmtDate(slot.loading_date)}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className="font-bold" style={{ color: '#065f46' }}>{slot.dos_allocated ?? 0}</span>
                            <span style={{ color: '#9ca3af' }}>/{slot.dos_available}</span>
                          </td>
                          <td className="px-3 py-2.5 text-center font-semibold" style={{ color: '#1e4a72' }}>{count}</td>
                          <td className="px-3 py-2.5 text-center"><Badge status={slot.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RATE CONFIG ────────────────────────────────────────────────── */}
      {subTab === 'rate' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border shadow-sm p-4 space-y-3" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Set Rate</h3>
            <div>
              <label className={lbl} style={{ color: '#374151' }}>Config Type</label>
              <select value={rateForm.config_type} onChange={e => setRateForm(p => ({ ...p, config_type: e.target.value }))} className={ic} style={is}>
                <option value="global">Global Base Rate</option>
                <option value="factory">Factory Delta</option>
                <option value="score_band">Score Band Delta</option>
              </select>
            </div>
            {rateForm.config_type === 'factory' && (
              <div>
                <label className={lbl} style={{ color: '#374151' }}>Factory</label>
                <select value={rateForm.factory_id} onChange={e => setRateForm(p => ({ ...p, factory_id: e.target.value }))} className={ic} style={is}>
                  <option value="">Select factory</option>
                  {factories.map(f => <option key={f.id} value={f.id}>{f.name} ({f.short_code})</option>)}
                </select>
              </div>
            )}
            {rateForm.config_type === 'score_band' && (
              <div>
                <label className={lbl} style={{ color: '#374151' }}>Score Band</label>
                <select value={rateForm.score_band} onChange={e => setRateForm(p => ({ ...p, score_band: e.target.value }))} className={ic} style={is}>
                  <option value="high">High — discount (negative)</option>
                  <option value="medium">Medium — flat</option>
                  <option value="low">Low — premium (positive)</option>
                </select>
              </div>
            )}
            <div>
              <label className={lbl} style={{ color: '#374151' }}>Paise {rateForm.config_type !== 'global' && '(delta, negative = discount)'}</label>
              <input type="number" value={rateForm.rate_paise} onChange={e => setRateForm(p => ({ ...p, rate_paise: e.target.value }))}
                placeholder="e.g. 100000 = ₹1000" className={ic} style={is} />
              {rateForm.rate_paise && (
                <p className="text-xs mt-1" style={{ color: '#6b7280' }}>= ₹{(parseInt(rateForm.rate_paise || '0') / 100).toLocaleString('en-IN')}</p>
              )}
            </div>
            <button onClick={saveRate} disabled={rateSaving}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: '#7c3aed' }}>
              {rateSaving ? <Spinner /> : <Icon name="Save" size={14} />}
              Save Config
            </button>
          </div>

          {/* Active configs */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
              <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Active Configs</h3>
            </div>
            {rateConfigs.length === 0
              ? <div className="text-center py-8 text-sm" style={{ color: '#6b7280' }}>No configs yet</div>
              : (
                <table className="w-full" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Type</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Applies To</th>
                      <th className="px-3 py-2 text-right font-semibold" style={{ color: '#6b7280' }}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateConfigs.map(rc => (
                      <tr key={rc.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                        <td className="px-3 py-2.5 font-semibold capitalize" style={{ color: '#1f2937' }}>{rc.config_type.replace('_', ' ')}</td>
                        <td className="px-3 py-2.5" style={{ color: '#374151' }}>
                          {rc.config_type === 'factory' && (factoryMap[rc.factory_id]?.name ?? rc.factory_id)}
                          {rc.config_type === 'score_band' && `${rc.score_band} band`}
                          {rc.config_type === 'global' && 'All traders'}
                        </td>
                        <td className="px-3 py-2.5 text-right font-bold" style={{ color: (rc.rate_paise ?? 0) < 0 ? '#dc2626' : '#065f46' }}>
                          {(rc.rate_paise ?? 0) < 0 ? '−' : '+'}{paise2inr(Math.abs(rc.rate_paise ?? 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}

      {/* ── PENALTIES ──────────────────────────────────────────────────── */}
      {subTab === 'penalties' && (
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
            <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>Penalty Log ({penalties.length})</h3>
          </div>
          {penalties.length === 0
            ? <div className="text-center py-10 text-sm" style={{ color: '#6b7280' }}>No penalties yet</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Trader</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Type</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Score Δ</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {penalties.map(p => {
                      const trader = traders[p.user_id];
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td className="px-3 py-2.5">
                            <button onClick={() => setDetailTrader(trader)} className="text-left">
                              <div className="font-semibold" style={{ color: '#1f2937' }}>{trader?.full_name ?? '—'}</div>
                              <div style={{ color: '#9ca3af' }}>{trader?.company_name ?? '—'}</div>
                            </button>
                          </td>
                          <td className="px-3 py-2.5 font-semibold capitalize" style={{ color: '#dc2626' }}>
                            {(p.penalty_type ?? '').replace(/_/g, ' ')}
                          </td>
                          <td className="px-3 py-2.5 text-center font-bold" style={{ color: '#dc2626' }}>
                            {p.trade_score_delta ? `−${Math.abs(p.trade_score_delta)}` : '—'}
                          </td>
                          <td className="px-3 py-2.5" style={{ color: '#6b7280' }}>{fmtDate(p.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}
      {/* ── BIDDERS ──────────────────────────────────────────────────── */}
      {subTab === 'bidders' && (
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
              <h3 className="font-bold text-sm" style={{ color: '#1f2937' }}>
                DO Finance Bidders ({applications.length})
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>All traders who have completed DO Finance assessment</p>
            </div>
            {applications.length === 0 ? (
              <div className="text-center py-10 text-sm" style={{ color: '#6b7280' }}>No applications yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>Trader</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>GSTIN</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Sugar</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Trade</th>
                      <th className="px-3 py-2 text-right font-semibold" style={{ color: '#6b7280' }}>Limit Req</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Vehicle Limit</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Status</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Bids</th>
                      <th className="px-3 py-2 text-center font-semibold" style={{ color: '#6b7280' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map(app => {
                      const trader = traders[app.user_id];
                      const eval_ = evaluations[app.id];
                      const traderBids = bids.filter(b => b.user_id === app.user_id);
                      const activeBid = traderBids.find(b => b.status === 'pending' || b.status === 'allocated');
                      const SUGAR_BAND_COLOR = { high: '#065f46', medium: '#92400e', low: '#991b1b' };
                      const SUGAR_BAND_BG   = { high: '#d1fae5', medium: '#fef3c7', low: '#fee2e2' };
                      const SUGAR_BAND_LABEL = { high: '🏆 Gold', medium: '🥈 Silver', low: '🥉 Bronze' };
                      const TRADE_BAND_COLOR = { high: '#1e40af', medium: '#374151', low: '#6b7280' };
                      const TRADE_BAND_BG   = { high: '#dbeafe', medium: '#f3f4f6', low: '#f9fafb' };
                      const TRADE_BAND_LABEL = { high: 'Elite', medium: 'Trusted', low: 'Active' };
                      const sb = eval_?.sugar_score_band ?? 'low';
                      const tb = eval_?.trade_score_band ?? 'low';
                      const isLimitActive = limitAction?.app_id === app.id;
                      return (
                        <React.Fragment key={app.id}>
                        <tr style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td className="px-3 py-2.5">
                            <button onClick={() => setDetailTrader(trader)} className="text-left">
                              <div className="font-semibold" style={{ color: '#1f2937' }}>{trader?.full_name ?? '—'}</div>
                              <div style={{ color: '#6b7280' }}>{trader?.company_name ?? '—'}</div>
                            </button>
                          </td>
                          <td className="px-3 py-2.5 font-mono" style={{ color: '#374151', fontSize: '11px' }}>
                            {app.gstin ?? trader?.gstin ?? '—'}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {eval_ ? (
                              <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold"
                                style={{ backgroundColor: SUGAR_BAND_BG[sb], color: SUGAR_BAND_COLOR[sb] }}>
                                {SUGAR_BAND_LABEL[sb]} {eval_.sugar_score}
                              </span>
                            ) : <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {eval_ ? (
                              <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold"
                                style={{ backgroundColor: TRADE_BAND_BG[tb], color: TRADE_BAND_COLOR[tb] }}>
                                {TRADE_BAND_LABEL[tb]} {eval_.trade_score}
                              </span>
                            ) : <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold" style={{ color: '#1f2937' }}>
                            {app.requested_limit_amount
                              ? `₹${(app.requested_limit_amount / 100000).toFixed(1)}L`
                              : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {app.approved_vehicle_limit ? (
                              <>
                                <div className="font-bold" style={{ color: '#1e4a72' }}>{app.approved_vehicle_limit}</div>
                                <div className="text-xs capitalize" style={{ color: app.limit_status === 'suspended' ? '#dc2626' : '#065f46' }}>{app.limit_status}</div>
                              </>
                            ) : <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-center"><Badge status={app.status} /></td>
                          <td className="px-3 py-2.5 text-center font-semibold" style={{ color: '#1e4a72' }}>{traderBids.length}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-col gap-1 items-center">
                              {(app.status === 'scored' || app.status === 'admin_review') && (
                                <>
                                  {app.status === 'scored' && (
                                    <button onClick={() => doLimitAction('trader-od-admin-review', { application_id: app.id, action: 'review' })}
                                      disabled={limitActionLoading}
                                      className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                      style={{ backgroundColor: '#374151' }}>
                                      Review
                                    </button>
                                  )}
                                  <button onClick={() => toggleLimitAction(app, 'approve')}
                                    className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                    style={{ backgroundColor: isLimitActive && limitAction?.type === 'approve' ? '#374151' : '#065f46' }}>
                                    Approve
                                  </button>
                                  <button onClick={() => toggleLimitAction(app, 'reject')}
                                    className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                    style={{ backgroundColor: isLimitActive && limitAction?.type === 'reject' ? '#374151' : '#dc2626' }}>
                                    Reject
                                  </button>
                                </>
                              )}
                              {app.status === 'approved' && app.limit_status === 'active' && (
                                <>
                                  <button onClick={() => toggleLimitAction(app, 'increase')}
                                    className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                    style={{ backgroundColor: isLimitActive && limitAction?.type === 'increase' ? '#374151' : '#065f46' }}>
                                    Increase
                                  </button>
                                  <button onClick={() => toggleLimitAction(app, 'decrease')}
                                    className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                    style={{ backgroundColor: isLimitActive && limitAction?.type === 'decrease' ? '#374151' : '#92400e' }}>
                                    Decrease
                                  </button>
                                  <button onClick={() => { if (window.confirm(`Suspend vehicle limit for ${trader?.full_name ?? 'this trader'}?`)) doLimitAction('trader-od-limit-adjust', { application_id: app.id, action: 'suspend' }); }}
                                    disabled={limitActionLoading}
                                    className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                    style={{ backgroundColor: '#dc2626' }}>
                                    Suspend
                                  </button>
                                </>
                              )}
                              {app.status === 'approved' && app.limit_status === 'suspended' && (
                                <button onClick={() => { if (window.confirm(`Reactivate vehicle limit for ${trader?.full_name ?? 'this trader'}?`)) doLimitAction('trader-od-limit-adjust', { application_id: app.id, action: 'reactivate' }); }}
                                  disabled={limitActionLoading}
                                  className="w-20 py-1 rounded-lg text-xs font-bold text-white text-center"
                                  style={{ backgroundColor: '#065f46' }}>
                                  Reactivate
                                </button>
                              )}
                              {app.status === 'rejected' && <span style={{ color: '#d1d5db' }}>—</span>}
                            </div>
                          </td>
                        </tr>

                        {isLimitActive && (limitAction.type === 'approve' || limitAction.type === 'increase' || limitAction.type === 'decrease') && (
                          <tr>
                            <td colSpan={9} className="px-3 pb-3">
                              <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                <span className="text-xs font-medium" style={{ color: '#374151' }}>
                                  {limitAction.type === 'approve' ? 'Vehicle limit:' : 'New vehicle limit:'}
                                </span>
                                <input type="number" min="1"
                                  value={vehicleLimitInput} onChange={e => setVehicleLimitInput(e.target.value)}
                                  className="w-20 px-2 py-1 rounded-lg border text-xs text-center font-bold"
                                  style={{ borderColor: '#6ee7b7' }} />
                                <button onClick={() => doLimitAction(
                                    limitAction.type === 'approve' ? 'trader-od-admin-review' : 'trader-od-limit-adjust',
                                    { application_id: app.id, action: limitAction.type, vehicle_limit: parseInt(vehicleLimitInput || '0') }
                                  )}
                                  disabled={limitActionLoading || !vehicleLimitInput}
                                  className="px-3 py-1 rounded-lg text-xs font-bold text-white flex items-center gap-1"
                                  style={{ backgroundColor: '#065f46' }}>
                                  {limitActionLoading ? <Spinner /> : '✓'} Confirm
                                </button>
                                <button onClick={() => setLimitAction(null)} className="text-xs" style={{ color: '#9ca3af' }}>×</button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {isLimitActive && limitAction.type === 'reject' && (
                          <tr>
                            <td colSpan={9} className="px-3 pb-3">
                              <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                                <input type="text" placeholder="Rejection reason (optional)"
                                  value={rejectReasonInput} onChange={e => setRejectReasonInput(e.target.value)}
                                  className="flex-1 px-2 py-1 rounded-lg border text-xs"
                                  style={{ borderColor: '#fca5a5' }} />
                                <button onClick={() => doLimitAction('trader-od-admin-review', { application_id: app.id, action: 'reject', rejection_reason: rejectReasonInput || undefined })}
                                  disabled={limitActionLoading}
                                  className="px-3 py-1 rounded-lg text-xs font-bold text-white flex items-center gap-1"
                                  style={{ backgroundColor: '#dc2626' }}>
                                  {limitActionLoading ? <Spinner /> : '✓'} Confirm
                                </button>
                                <button onClick={() => setLimitAction(null)} className="text-xs" style={{ color: '#9ca3af' }}>×</button>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default DoFinanceTab;
